/**
 * Lightweight drag-and-drop list wrapper around @dnd-kit/sortable.
 *
 * Designed for admin lists where items have stable string IDs and a small
 * "handle" (e.g. a GripVertical icon) for grabbing the row. Keyboard a11y
 * comes for free via @dnd-kit's KeyboardSensor.
 *
 * Usage:
 *   <SortableList items={['a','b','c']} onReorder={(newOrder) => save(newOrder)}>
 *     {(id) => <SortableItem id={id}><Row /></SortableItem>}
 *   </SortableList>
 *
 * The child render-prop receives each item id and must return a
 * <SortableItem id={id}>...</SortableItem> wrapper.
 */
import React from 'react';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor,
    useSensor, useSensors,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

export function SortableList({ items, onReorder, children, disabled = false }) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(items, oldIndex, newIndex));
    };

    if (disabled) {
        return <>{items.map((id) => children(id))}</>;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {items.map((id) => children(id))}
            </SortableContext>
        </DndContext>
    );
}

export function SortableItem({ id, className = '', children, handleTestid }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} className={`${className} ${isDragging ? 'z-10' : ''}`}>
            <button
                type="button"
                className="shrink-0 cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-700 p-1 -ml-1"
                aria-label="Zum Verschieben anfassen"
                data-testid={handleTestid}
                {...attributes}
                {...listeners}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            {children}
        </div>
    );
}
